import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ProductQuickView from '../components/ProductQuickView';
import Testimonials from '../components/Testimonials';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryPage = ({ category }) => {
  const [products, setProducts] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [category]);

  const fetchCategoryProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products?category=${category}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching category products:', error);
    }
  }, [category]);

  useEffect(() => {
    fetchCategoryProducts();
  }, [fetchCategoryProducts]);

  const getCategoryTitle = () => {
    if (category === 'woodwork_and_art') return 'Woodwork & Art';
    if (category === 'mandirs') return 'Bespoke Mandirs';
    if (category === 'copperware') return 'Artisan Metals';
    if (category === 'candles') return 'Ayurvedic Aromas';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const BackToCollections = () => (
    <button 
      onClick={() => {
        navigate('/', { state: { scrollTo: 'collections', t: Date.now() } });
      }}
      className="inline-flex items-center gap-2 text-[#B87333] hover:text-[#A05A20] transition-colors font-medium text-sm"
      data-testid="back-to-collections"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to Collections
    </button>
  );

  return (
    <div data-testid="category-page">
      <div className="pt-6 pb-20 md:pt-8 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <BackToCollections />
          </div>
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="category-title">
              {getCategoryTitle()}
            </h1>
            <p className="text-lg text-[#5D4037] max-w-2xl mx-auto" data-testid="category-description">
              {category === 'woodwork_and_art' && 'Handcrafted wooden products and artisan artwork for your home.'}
              {category === 'mandirs' && 'Bespoke home temples designed to bring sacred beauty to modern living. Local delivery or pickup only.'}
              {category === 'copperware' && 'Handcrafted metal products with centuries of tradition and timeless elegance.'}
              {category === 'candles' && 'Incense-inspired candles crafted with Ayurvedic fragrances and clean-burning soy wax.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="category-products-grid">
            {products.map(product => (
              <ProductCard key={product.id} product={product} onQuickView={() => setQuickViewProduct(product)} />
            ))}
          </div>

          <div className="mt-16 text-center">
            <BackToCollections />
          </div>
        </div>
      </div>

      <Testimonials category={category} />

      <ProductQuickView
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
      />
    </div>
  );
};

export default CategoryPage;
